import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';
import { FONT, MEDIA_QUERY } from '../../styles/emotion';
import COLORS, { COLORS_SEMANTIC } from '../../styles/emotion/colors3';
import SPACING from '../../styles/emotion/spacing';
import { LinkV2 } from '../LinkV2';

export const fieldHeight = 44;
export const fieldHeightMobile = 52;

export const Asterisk = styled.span`
  color: ${COLORS_SEMANTIC.NEGATIVE_100};
  margin-left: 3px;
`;

export type State = 'success' | 'warn' | 'error' | '';

const getStateColor = (state?: State): string => {
  if (state === 'success') {
    return COLORS_SEMANTIC.POSITIVE_100;
  }
  if (state === 'warn') {
    return COLORS_SEMANTIC.WARNING_100;
  }
  if (state === 'error') {
    return COLORS_SEMANTIC.NEGATIVE_100;
  }
  return `var(--text-primary, ${COLORS.BLACK_100})`;
};
const inputBorderStyling = (color: string) => `border: 1px solid ${color}`;

const renderInputBorder = (state?: State): string => {
  if (state) {
    return inputBorderStyling(getStateColor(state));
  }
  // Only show selected border when no specific state
  return `
      ${inputBorderStyling(`var(--border-element-color, ${COLORS.BLACK_030})`)};
      &:focus {
        border-color: var(--border-element-selected-color, ${COLORS.BLACK_100});
      }
    `;
};

export const disabledStyles = css`
  color: var(--system-disabled, ${COLORS.BLACK_030});
  background-color: var(--background-container, ${COLORS.BLACK_005});
  &::placeholder {
    color: var(--system-disabled, ${COLORS.BLACK_030});
  }
`;

export const inputBaseStyles = (state?: State): SerializedStyles => css`
  padding: 14px 12px;
  border-radius: var(--border-radius-xs, 4px);
  box-sizing: border-box;
  width: 100%;
  background-color: ${COLORS.WHITE_100};
  color: var(--text-primary, ${COLORS.BLACK_100});
  font-size: ${FONT.FONT_SIZE_SMALL};
  font-weight: ${FONT.FONT_WEIGHT_REGULAR};

  ${renderInputBorder(state)};

  &::placeholder {
    color: var(--text-tertiary, ${COLORS.BLACK_050});
  }
  &:disabled {
    ${disabledStyles}
  }
`;

export const helperTextLineHeight = 16;

export const HelperText = styled.span<{ state?: State }>`
  font-size: ${FONT.FONT_SIZE_EXTRA_SMALL};
  line-height: ${helperTextLineHeight}px;

  ${({ state }) => `color: ${getStateColor(state)};`}
`;

export const CharCountMessage = styled(HelperText)`
  margin-left: auto;
`;

export const MessageContainer = styled.div`
  display: flex;
  margin-top: 5px;
`;

export const FormLabel = styled.label`
  color: var(--text-primary, ${COLORS.BLACK_100});
  font-size: ${FONT.FONT_SIZE_SMALL};
  text-transform: none;
  letter-spacing: normal;
  font-weight: ${FONT.FONT_WEIGHT_REGULAR};
  // defensive styling for _core.css in web-wedding
  .form-label {
    margin-bottom: 0;
  }
  margin-bottom: 0;
`;

export const IconWrapper = styled.div`
  position: relative;
  top: -8px;
`;

const displayDesktop = css`
  display: inline-block;
  ${MEDIA_QUERY.EXCLUDE_DESKTOP} {
    display: none;
  }
`;

const displayNotDesktop = css`
  display: inline-block;

  ${MEDIA_QUERY.DESKTOP} {
    display: none;
  }
`;

const labelLinkStyles = (displayConditionally: boolean) => css`
  margin-left: 5px;
  ${displayConditionally && displayDesktop}
`;

const inputLinkStyles = (displayConditionally: boolean) => css`
  margin-top: 5px;
  ${displayConditionally && displayNotDesktop};
`;

// TODO: move to comone
export interface LabelLinkProps {
  url: string;
  copy: string | React.ReactNode;
}

export const renderLink = (
  displaySection: 'input' | 'label',
  displayConditionally: boolean,
  labelLink?: LabelLinkProps | React.ReactElement
): React.ReactNode => {
  if (!labelLink) {
    return null;
  }
  const cssStyle =
    displaySection === 'label'
      ? labelLinkStyles(displayConditionally)
      : inputLinkStyles(displayConditionally);
  if (React.isValidElement(labelLink)) {
    return <span css={cssStyle}>{labelLink}</span>;
  }

  return (
    <LinkV2
      css={cssStyle}
      href={(labelLink as unknown as LabelLinkProps).url}
      arrow
      sizes="small"
      noTextTransform
    >
      {(labelLink as unknown as LabelLinkProps).copy}
    </LinkV2>
  );
};

export const ListBoxGroupLabel = styled.li`
  && {
    color: var(--text-primary, ${COLORS.BLACK_100});
    cursor: default;
    font-weight: ${FONT.FONT_WEIGHT_REGULAR};
    line-height: 28px;
    padding: 8px ${SPACING.MD};
  }
`;

export const ListBoxOption = styled.li<{
  selected?: boolean;
  isPlaceholder?: boolean;
  shouldIndent?: boolean;
  focused?: boolean;
}>`
  && {
    ${FONT.textSizeCompact(FONT.SIZE.SMALL)}
    padding: ${SPACING.XS} ${SPACING.S16};
    &:first-of-type {
      padding-top: ${SPACING.SM};
    }
    &:last-of-type {
      padding-bottom: ${SPACING.SM};
    }
    &:hover {
      background-color: var(--background-container, ${COLORS.BLACK_005});
      color: var(--text-primary, ${COLORS.BLACK_100});
    }
    ${({ selected }) => selected && `font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};`}
    ${({ isPlaceholder }) => isPlaceholder && `color: var(--text-tertiary, ${COLORS.BLACK_050})`};
    ${({ shouldIndent }) => shouldIndent && `padding-left: ${SPACING.LG}`};
    ${({ focused }) =>
      focused &&
      `
      background-color: var(--background-container, ${COLORS.BLACK_010});
      color: var(--text-primary, ${COLORS.BLACK_100});
    `}
  }
`;
